import { useMutation } from '@tanstack/react-query';

import { Invoice } from '@/api/invoices';
import { PriceQuantityPair } from '@/api/subscriptions';
import { axios } from '@/common/axios';
import { MutationConfig } from '@/common/react-query';
import { APIResponse } from '@/common/types';

export type PaymentInvoiceResponse = APIResponse<{ invoice: Invoice }>;

export const createPaymentInvoice = async (): Promise<PaymentInvoiceResponse> => {
    return axios.post<PaymentInvoiceResponse, PaymentInvoiceResponse>('/api/v2/payments/invoices');
};

type UpdatePaymentInvoiceParams = {
    items: PriceQuantityPair[];
};

export const updatePaymentInvoice = async (
    { items }: UpdatePaymentInvoiceParams,
    invoiceId: string
): Promise<PaymentInvoiceResponse> => {
    return axios.put<PaymentInvoiceResponse, PaymentInvoiceResponse>(`/api/v2/payments/invoices/${invoiceId}`, {
        items,
    });
};

export const finalizePaymentInvoice = async (
    invoiceId: string,
    options: { notFromAccountBalance?: boolean } = {}
): Promise<PaymentInvoiceResponse> => {
    return axios.post<PaymentInvoiceResponse, PaymentInvoiceResponse>(
        `/api/v2/payments/invoices/${invoiceId}/finalize`,
        options
    );
};

export type UseUpdatePaymentInvoiceOptions = { invoiceId?: string } & {
    config?: MutationConfig<typeof updatePaymentInvoice>;
};

export const useUpdatePaymentInvoice = ({ invoiceId, config }: UseUpdatePaymentInvoiceOptions) => {
    return useMutation({
        ...config,
        mutationFn: ({ items }: { items: PriceQuantityPair[] }) => {
            if (!invoiceId) throw new Error('cannot update payment invoice without invoiceId');
            return updatePaymentInvoice({ items }, invoiceId);
        },
        scope: {
            id: invoiceId ?? '',
        },
    });
};
